import React, { useEffect, useMemo, useRef } from "react";
import { ReelCanvas, type ReelHandle } from "./ReelCanvas.js";
import project from "@hudhud/reel-rv/scene";
import { FPS, WIDTH, HEIGHT, ARABIC_FONTS, type ReelProps } from "@hudhud/reel";
import { localiseClip, voiceProcessed, type Clip } from "@hudhud/core";
import { useEditor, langFontDefaults } from "./store.js";
import { EditOverlay } from "./EditOverlay.js";
import { NativePlayer } from "./NativePlayer.js";
import { LiveSound } from "./LiveSound.js";
import type { ClipFiles } from "../api.js";

const SafeZone: React.FC = () => <>
  <div style={{ position: "absolute", left: "4%", right: "4%", top: "12%", bottom: "15%", border: "1px dashed rgba(247,245,239,0.4)", borderRadius: 8, pointerEvents: "none" }} />
  <span style={{ position: "absolute", right: "4%", top: "9%", fontSize: 10, color: "rgba(247,245,239,0.7)", letterSpacing: 0.5 }}>SAFE ZONE</span>
</>;

/**
 * The preview. Normally the instant player: one streamed video and light DOM on top, ready the moment the clip opens on any
 * device. "Exact" swaps in the render's own scene drawn live by Revideo's player — every transition, grade and effect as the
 * render draws them, edits visible at once — with the studio's live sound mix over it.
 */
export const Preview: React.FC<{ width: number }> = ({ width }) => {
  const clip = useEditor((s) => s.clip);
  const files = useEditor((s) => s.files);
  const media = useEditor((s) => s.media);
  const safeZone = useEditor((s) => s.safeZone);
  const exact = useEditor((s) => s.exactPreview);
  const previewLang = useEditor((s) => s.previewLang);
  // the preview can show a language version: same cuts and framing, translated captions/opener/end line
  const shown = useMemo(() => (clip ? localiseClip(clip, previewLang, { arabicFonts: ARABIC_FONTS, langFonts: langFontDefaults() }) : null), [clip, previewLang]);
  const root = useRef<HTMLDivElement>(null);
  const height = Math.round((width * HEIGHT) / WIDTH);
  if (!clip || !shown || !files) return null;
  return (
    <div className="preview-frame" style={{ width, height }}>
      {exact ? <LivePreview clip={shown} files={files} width={width} height={height} rootRef={root} /> : <NativePlayer clip={shown} files={files} media={media} width={width} height={height} rootRef={root} />}
      <EditOverlay width={width} height={height} playerRoot={root} />
      {safeZone ? <SafeZone /> : null}
    </div>
  );
};

/**
 * The exact preview: the reel's scene, the same one the renderer draws, run live by Revideo's core player on a canvas. Its picture
 * is silent — LiveSound plays the sound in step with it. The player keeps its own clock while it plays and reports the time;
 * a seek from elsewhere (transcript click, timeline scrub, frame step) is sent to it directly.
 */
const LivePreview: React.FC<{ clip: Clip; files: ClipFiles; width: number; height: number; rootRef: React.RefObject<HTMLDivElement | null> }> = ({ clip: shown, files, width, height, rootRef }) => {
  const media = useEditor((s) => s.media);
  const playing = useEditor((s) => s.playing);
  const loop = useEditor((s) => s.loop);
  const speed = useEditor((s) => s.speed);
  const audition = useEditor((s) => s.audition);
  const { setPlayhead, setPlaying } = useEditor.getState();
  const player = useRef<ReelHandle | null>(null);
  const clock = useRef(0); // the player's time, ms
  const reported = useRef(-1); // the last time the player reported, so its own reports never read as a seek
  const lastWrite = useRef(0);
  const duration = useRef(0);

  const cleanOn = voiceProcessed(shown);
  const base = (cleanOn && files.mediaClean) || files.media || null;
  const cleanTag = cleanOn && base?.includes("-clean") ? `_c${shown.voiceClean?.enabled ? shown.voiceClean.amount : "off"}_${shown.voiceClean?.sampleStartMs ?? "auto"}${shown.voiceClean?.enhance ? "_e" : ""}` : "";
  const mediaUrl = base && media ? `${base}?v=${media.startMs}_${media.endMs}${cleanTag}` : null;
  const props = useMemo<ReelProps | null>(() => (mediaUrl ? {
    clip: shown, mediaUrl, mediaStartMs: media?.startMs ?? 0,
    logoUrl: files.logo?.url ?? "/assets/logo.png", logoSet: files.logo?.set, fontsBaseUrl: "/assets/fonts",
    overlaysBaseUrl: files.overlaysBase ?? undefined,
    // the cutout and the source are two players that drift by a frame or two while playing: drawn only while paused (the render is exact)
    cutoutUrl: playing ? undefined : files.cutout ?? undefined,
    settleOpener: !playing, lite: false, player: true,
  } : null), [shown, mediaUrl, media?.startMs, files.overlaysBase, files.cutout, files.logo, playing]);
  const variables = useMemo(() => (props ? { props } : {}), [props]);

  const frameOf = (ms: number) => Math.max(0, Math.round((ms / 1000) * FPS));
  // seeks: the store's playhead moving away from what the player itself reported
  useEffect(() => useEditor.subscribe((s, prev) => {
    if (s.playheadMs === prev.playheadMs || !player.current) return;
    const off = Math.abs(s.playheadMs - reported.current);
    if (off > (s.playing ? 150 : 1000 / FPS / 2)) { reported.current = s.playheadMs; clock.current = s.playheadMs; player.current.seek(frameOf(s.playheadMs)); }
  }), []);
  const onTime = (seconds: number) => {
    const ms = seconds * 1000;
    clock.current = ms; reported.current = ms;
    // the player keeps its own clock; the rest of the UI only needs ~12 updates a second
    const now = performance.now();
    const st = useEditor.getState();
    if (now - lastWrite.current > 80 || !st.playing) { lastWrite.current = now; if (Math.abs(ms - st.playheadMs) >= 1000 / FPS / 2) setPlayhead(ms); }
    if (st.playing && !st.loop && duration.current > 0 && ms >= duration.current * 1000 - 1000 / FPS) setPlaying(false);
  };
  const onReady = (reel: ReelHandle) => { player.current = reel; reel.seek(frameOf(useEditor.getState().playheadMs)); };

  if (!props) return <div ref={rootRef} style={{ width, height, background: "#0B0F0D" }} />;
  return (
    <div ref={rootRef} className="live-preview" style={{ width, height, position: "relative", background: "#0B0F0D" }}>
      <ReelCanvas project={project} variables={variables} fps={FPS} playing={playing} looping={loop} speed={speed} width={width} height={height} onTime={onTime} onDuration={(d) => { duration.current = d; }} onReady={onReady} />
      <LiveSound clip={shown} files={files} playing={playing} speed={speed} audition={audition} clock={clock} />
    </div>
  );
};
